import { i18n } from 'boot/i18n';
import { getFilterCriteriaFromColumns, hydrateFilters } from 'src/utils/FilterUtils';
import { computed, ref } from 'vue';

import { countCohortCriteria } from '../../domain/services/cohortService';
import { useCohortsAnalytics } from './useCohortsAnalytics';
import { useCohortsFiltersColumns } from './useCohortsFiltersColumns';

/** @typedef {
 *   import('../../domain/entities/CareModelCohort.types').CareModelCohort
 * } CareModelCohort */

/**
 * Editable state and filter handling behind the cohort dialog.
 *
 * @param {Object} args
 * @param {CareModelCohort} args.cohort - Cohort being edited, empty when a new one is created.
 * @param {string} args.cohortEditAttemptId - ID shared by every analytics event of this dialog.
 */
// eslint-disable-next-line import/prefer-default-export
export function useEditCohortDialog({ cohort, cohortEditAttemptId }) {
  const { columns, columnGroups } = useCohortsFiltersColumns();
  const { trackCohortCriterionAdded, trackCohortCriterionRemoved } = useCohortsAnalytics();

  const touched = ref(false);

  const editCohort = ref({
    ...cohort,
    filters: hydrateFilters(cohort?.filters || {}, columns.value),
  });

  const isEditing = !!cohort?.id;

  const filterCriteria = computed(() => {
    return getFilterCriteriaFromColumns(columns.value, false);
  });

  const cohortFilters = computed(() => editCohort.value.filters || {});

  const filtersCount = computed(() => countCohortCriteria(cohortFilters.value));

  const canAddFilter = computed(() => {
    const availableFilterKeys = filterCriteria.value.map((criteria) => criteria.value);
    const usedFilterKeys = Object.keys(cohortFilters.value);
    return availableFilterKeys.some((key) => !usedFilterKeys.includes(key));
  });

  function normalizeCohortName(value) {
    return value?.trim?.() ?? '';
  }

  const validators = {
    requiredTrimmed(val) {
      return !!normalizeCohortName(val) || i18n.t('common.validationRequired');
    },
    atLeastOneFilter(val) {
      return val > 0 || i18n.t('settings.careModel.cohorts.edit.filtersRequired');
    },
  };

  const rules = {
    cohortName: [
      validators.requiredTrimmed,
    ],
    filters: [
      validators.atLeastOneFilter,
    ],
  };

  function firstRuleError(value, ruleList = []) {
    return ruleList.map((rule) => rule(value)).find((result) => result !== true) ?? null;
  }

  const hasValidationError = computed(() => {
    return firstRuleError(editCohort.value.cohortName, rules.cohortName) !== null
      || firstRuleError(filtersCount.value, rules.filters) !== null;
  });

  const isSubmitDisabled = computed(() => !touched.value || hasValidationError.value);

  /*
   * Kept apart from `touched`, which typing a name also sets: the filters are not a form field, so
   * nothing else holds their error back until the reader has had a chance to add one.
   */
  const areFiltersTouched = ref(false);

  const filtersError = computed(() => {
    if (!areFiltersTouched.value) {
      return null;
    }
    return firstRuleError(filtersCount.value, rules.filters);
  });

  function touchFilters() {
    areFiltersTouched.value = true;
  }

  function setTouched() {
    touched.value = true;
  }

  /**
   * @returns {CareModelCohort} - Cohort with normalized values, ready to be saved
   */
  function prepareCohortForSave() {
    editCohort.value.cohortName = normalizeCohortName(editCohort.value.cohortName);
    return editCohort.value;
  }

  /**
   * @param {Object} statement - Criterion as picked in the filter UI
   * @param {string} filterKey - Key of the filter the criterion belongs to
   * @param {boolean} isReplacement - Whether the criterion replaced an existing one
   * @returns {Object} - Params for the criterion analytics events
   */
  function getCriterionEventParams(statement, filterKey, isReplacement) {
    return {
      cohortEditAttemptId,
      cohortId: cohort?.id || null,
      filterKey,
      statement,
      filters: editCohort.value.filters,
      isReplacement,
    };
  }

  function statementsOf(filters) {
    return Object.entries(filters ?? {}).flatMap(([filterKey, filter]) => (
      (filter?.filterStatements ?? []).map((statement) => ({ filterKey, statement }))
    ));
  }

  /*
   * Selected values are keyed sorted because the filter engine matches on the set, not the order:
   * deselecting a value and picking it again reorders the array without changing what the
   * criterion executes, and an order-sensitive key would report that as a removal and an addition.
   */
  function itemsKey(items) {
    return Array.isArray(items)
      ? items.map((item) => JSON.stringify(item ?? null)).sort()
      : null;
  }

  /*
   * Identity is what a statement executes, not the label its chip reads. A match-all filter writes
   * one statement per value, and two of those can carry the same label or none at all, so a label
   * key would treat distinct criteria as the same one.
   */
  function statementKey({ filterKey, statement }) {
    return JSON.stringify([
      filterKey,
      statement?.filterCriteriaType ?? null,
      statement?.filterNegative ?? false,
      itemsKey(statement?.filteredItems),
      statement?.fromDate ?? null,
      statement?.toDate ?? null,
      itemsKey(statement?.filterDateItems),
    ]);
  }

  function countByKey(entries) {
    return entries.reduce((counts, entry) => {
      const key = statementKey(entry);
      counts.set(key, (counts.get(key) ?? 0) + 1);
      return counts;
    }, new Map());
  }

  /*
   * Counted rather than set-differenced: two identical statements are two criteria, so dropping
   * one of them has to report a removal rather than cancel out against the one still there.
   */
  function surplus(entries, otherCounts) {
    const remaining = new Map(otherCounts);

    return entries.filter((entry) => {
      const key = statementKey(entry);
      const available = remaining.get(key) ?? 0;

      if (available === 0) {
        return true;
      }

      remaining.set(key, available - 1);
      return false;
    });
  }

  /*
   * The bar reports the whole map, so what was added or removed is worked out by comparing it
   * with what was there. Each criterion still reports itself individually, since that is what the
   * analytics measure.
   */
  function onFiltersChanged(filters) {
    const before = statementsOf(editCohort.value.filters);
    const after = statementsOf(filters);

    const removed = surplus(before, countByKey(after));
    const added = surplus(after, countByKey(before));

    editCohort.value.filters = filters;
    setTouched();
    touchFilters();

    removed.forEach(({ filterKey, statement }) => trackCohortCriterionRemoved(
      getCriterionEventParams(statement, filterKey, false),
    ));

    added.forEach(({ filterKey, statement }) => trackCohortCriterionAdded(
      getCriterionEventParams(statement, filterKey, false),
    ));
  }

  return {
    editCohort,
    cohortFilters,
    filtersCount,
    canAddFilter,
    isEditing,
    isSubmitDisabled,
    rules,
    touched,
    setTouched,
    filtersError,
    touchFilters,
    prepareCohortForSave,
    onFiltersChanged,
    filterCriteria,
    columnGroups,
  };
}
